import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const RetakePage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([])
  const [remainingTime, setRemainingTime] = useState<number>(60)

  const { config } = useConfigStore()
  const { gallery, setSessionState, uploadSessionState } = useSessionStateStore()
  const { cameraSettings } = useSettingsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number => {
    if (cameraSettings.captureRatio === 'auto') return 1 // Default to 1:1 for auto
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 1 // Default fallback
  }

  // Calculate container dimensions based on capture ratio
  const containerSize = 269 // Base size in pixels
  const ratio = getNormalizedCaptureRatio()
  const containerStyle = {
    width: ratio >= 1 ? containerSize : containerSize * ratio,
    height: ratio >= 1 ? containerSize / ratio : containerSize
  }

  const maxRetakeCount = config?.pages.retake?.settings?.maxRetakeCount || 3
  const retakeTimeout = config?.pages.retake?.settings?.retakeTimeout || 20

  // Initialize countdown hook
  const retakeCountdown = useCountdown(retakeTimeout)

  // Filter out only items with photoUrl
  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  useEffect((): (() => void) => {
    retakeCountdown.setOnTick(() => {
      setRemainingTime(retakeCountdown.getRemainingSeconds())
    })

    retakeCountdown.setOnComplete(() => {
      continueToNextPage()
    })

    retakeCountdown.start()

    return () => {
      retakeCountdown.stop()
    }
  }, [])

  const handlePhotoClick = (index: number): void => {
    setSelectedPhotos((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index)
      } else if (prev.length < maxRetakeCount) {
        return [...prev, index]
      }
      return prev
    })
  }

  const handleRetake = async (): Promise<void> => {
    retakeCountdown.stop()

    const remainingItems = validPhotos.filter((_, index) => !selectedPhotos.includes(index))

    // Navigate to capture page for retakes
    navigate(`/${projectId}/capture`)

    // Update session state with remaining photos
    await setSessionState({
      userPreferences: {
        retakeCount: selectedPhotos.length
      },
      gallery: {
        capturedItems: remainingItems
      },
      timeline: {
        activityLogs: [
          {
            page: 'retake',
            type: 'output',
            action: 'delete',
            details: {
              count: selectedPhotos.length
            },
            timestamp: new Date()
          }
        ]
      }
    })

    // Upload session state before navigating
    uploadSessionState()
  }

  const continueToNextPage = async (): Promise<void> => {
    uploadSessionState()
    navigate(`/${projectId}/select`)
  }

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-retake.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo-white.png')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedBackground})` }}
    >
      <img
        alt="Samu Logo"
        src={cachedLogo}
        width={200}
        height={200}
        className="absolute top-0 right-6"
      />
      {/* Photo Grid */}
      <div className="absolute right-0 top-12 w-6/10 h-full flex flex-col justify-center">
        <div className="grid grid-cols-3 gap-8 px-16">
          {validPhotos.map((item, index) => {
            const isSelected = selectedPhotos.includes(index)
            return (
              <div
                key={index}
                className="relative"
                style={{
                  width: containerStyle.width,
                  height: containerStyle.height
                }}
              >
                <div
                  className={`absolute inset-0 ${
                    isSelected ? 'border-[8px] rounded-xl border-orangePop' : ''
                  }`}
                >
                  <img
                    src={item.photoUrl}
                    className="w-full h-full cursor-pointer"
                    style={{ objectFit: 'cover' }}
                    onClick={() => handlePhotoClick(index)}
                    alt={`Captured ${index + 1}`}
                  />
                </div>
                {isSelected && (
                  <div className="absolute top-4 right-4 w-[34px] h-[34px] rounded-full border-2 border-orangePop flex items-center justify-center z-10">
                    <span className="text-orangePop text-2xl">✓</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-8 justify-center mt-12">
          <button
            onClick={handleRetake}
            className={`rounded-lg bg-carnabyTan text-foundationWhite font-body-samu tracking-widest px-8 py-6 text-3xl ${
              selectedPhotos.length === 0 ? 'opacity-30' : 'opacity-100'
            }`}
            disabled={selectedPhotos.length === 0}
          >
            Foto Ulang
          </button>
          <button
            onClick={continueToNextPage}
            className={`rounded-lg bg-carnabyTan text-foundationWhite font-body-samu tracking-widest px-8 py-6 text-3xl ${
              selectedPhotos.length === 0 ? 'opacity-100' : 'opacity-30'
            }`}
            disabled={selectedPhotos.length > 0}
          >
            Lanjut ({remainingTime})
          </button>
        </div>
      </div>
    </div>
  )
}

export default RetakePage
